import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo.ts';

const LAST_UPDATED = '2026-05-22';

/**
 * Minimum-viable Terms of Service. Covers: acceptable use, what we provide vs don't,
 * payment + refunds, account termination, liability cap, governing law. Not a substitute
 * for a lawyer-drafted document, but it's the floor an EU SaaS needs to ship.
 */
export default function TermsPage() {
  useSeo({
    title: 'Terms of service',
    canonicalPath: '/terms',
    description:
      'EAA Proof terms of service: scope of the scan product, payment and refund terms, EU consumer rights, liability boundaries, and governing law.',
  });

  return (
    <div className="page legal-page">
      <header>
        <h1>Terms of service</h1>
        <p className="muted">Last updated: {LAST_UPDATED}</p>
      </header>

      <section>
        <p>
          These terms govern your use of <strong>eaaproof.com</strong> (the "Service") provided
          by EAA Proof ("we", "us"). By creating an account or using the Service you agree to
          these terms. If you do not agree, do not use the Service.
        </p>
      </section>

      <section>
        <h2>1. What the Service does</h2>
        <p>
          We scan a website you provide for European Accessibility Act (Directive 2019/882)
          compliance, produce a WCAG 2.1 AA audit report, and generate a draft accessibility
          statement. The output is intended as an automated first-pass assessment — it is
          NOT a legal opinion and does not replace manual review by an accessibility expert
          or a lawyer. Compliance with the EAA depends on facts and judgement an automated
          scan cannot fully assess.
        </p>
      </section>

      <section>
        <h2>2. Your account</h2>
        <p>
          You must be at least 18 and provide accurate sign-up information. You are
          responsible for activity under your account. Don't share credentials. Notify us if
          you suspect unauthorised access.
        </p>
      </section>

      <section>
        <h2>3. Acceptable use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Submit URLs for scanning that you do not own or have permission to scan.</li>
          <li>
            Use the Service to attack, probe, or test the security of any website (the
            Service is an accessibility scanner, not a penetration testing tool).
          </li>
          <li>
            Resell, mirror, or scrape the Service's output as if it were your own product
            without our written consent. Agency plans grant rebrand rights — see pricing.
          </li>
          <li>
            Interfere with, overload, or disrupt the Service (e.g. queueing thousands of
            scans against the same site to abuse the credit system).
          </li>
          <li>Use the Service for any unlawful purpose.</li>
        </ul>
      </section>

      <section>
        <h2>4. Payment, credits, and refunds</h2>
        <p>
          Pricing is shown on the <Link to="/pricing">pricing page</Link>. Single-scan
          purchases are one-time payments; "Basic" and "Agency" are monthly subscriptions
          that grant a fixed credit allotment each month. Unused credits do not roll over.
        </p>
        <p>
          Each scan reserves one credit at submission. If the scan fails for technical
          reasons on our side, the credit is automatically refunded. We do not refund scans
          that complete successfully, including scans that return zero violations or scans
          you didn't intend to run — review the URL before submitting.
        </p>
        <p>
          You may cancel a subscription at any time via your{' '}
          <Link to="/profile">profile</Link>. Cancellation stops future billing; you keep
          access to the remaining credits for the current billing period.
        </p>
        <p>
          EU consumers: under the Consumer Rights Directive (2011/83/EU), you have a 14-day
          right of withdrawal after subscribing to a service. However, by submitting a scan
          you expressly request that the service begin during the withdrawal period and
          acknowledge that, once the scan completes, your right of withdrawal for that scan
          is lost.
        </p>
      </section>

      <section>
        <h2>5. Intellectual property</h2>
        <p>
          We retain all rights to the Service software and to the report templates. You
          retain all rights to the content of your scanned sites. The PDF report and HTML
          accessibility statement generated for a scan are yours to use, modify, and publish.
        </p>
      </section>

      <section>
        <h2>6. Disclaimer</h2>
        <p>
          The Service is provided "as is" without warranties of any kind. We do not warrant
          that the Service will be uninterrupted, error-free, or that any scan will detect
          every accessibility issue. Automated accessibility scanning typically detects
          30–50% of WCAG issues; the rest require manual review. We are not responsible for
          regulatory or legal outcomes arising from reliance on a scan.
        </p>
      </section>

      <section>
        <h2>7. Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, our total liability arising out of or
          relating to the Service is capped at the amount you paid us in the 12 months
          preceding the event giving rise to the claim. We are not liable for indirect,
          incidental, or consequential damages.
        </p>
        <p>
          Nothing in these terms limits liability that cannot be limited by law, including
          for death, personal injury, fraud, or gross negligence.
        </p>
      </section>

      <section>
        <h2>8. Termination</h2>
        <p>
          You can delete your account at any time from your{' '}
          <Link to="/profile">profile</Link>. We can suspend or terminate accounts for
          material breach of these terms with notice where reasonable. On termination we
          delete your data per the schedule in the{' '}
          <Link to="/privacy">privacy policy</Link>.
        </p>
      </section>

      <section>
        <h2>9. Governing law</h2>
        <p>
          These terms are governed by the laws of the European Union and the member state in
          which we are established. Mandatory consumer-protection rules of your country of
          residence remain available to you.
        </p>
      </section>

      <section>
        <h2>10. Contact</h2>
        <p>
          Questions about these terms:{' '}
          <a href="mailto:support@eaaproof.com">support@eaaproof.com</a>.
        </p>
      </section>
    </div>
  );
}
